import {
  HemisphericLight,
  Color3,
  Vector3,
  Engine,
  CubeTexture,
  PBRMaterial,
  AbstractMesh,
  Material,
  MultiMaterial,
  VideoTexture,
  StandardMaterial,
} from 'babylonjs';
import 'babylonjs-loaders';
import 'babylonjs-materials';
import AppWebXRSimple from '@webcore/apps/app.webxr.simple';
import {
  XRModuleSessionOptions,
  XRModuleFloorEstimate,
  XRModuleFloorEstimateOptions,
} from '@webcore/apps/app.webxr.modules';
import { IAssetBase, IAssetModel } from '@webcore/core/loader/loader';
import emitter from '@webcore/core/events/eventemitter';
import appEvents from '@core/events/events';
import WSBridge, { WSBridgeOptions } from '@webcore/core/bridge-ws/bridge.ws';
import { WSBridgeCameraImageType, WSBridgeDataUpdatePolicy } from "@webcore/core/bridge-ws/bridge.ws.types";

// Core classes from boilerplate-core
import { SceneFadeController } from '@core/effects/SceneFadeController';
import { PlacementController } from '@core/controllers/Placement';
import { AnchorManager } from '@core/content/AnchorManager';
import { ContentInstanceManager } from '@core/content/ContentInstanceManager';
import { ContentPlaybackController } from '@core/content/ContentPlaybackController';
import { AppState } from '@core/context/types';
import getUserDevice from '@core/utils/getUserDevice';

// WebXR media recorder
import MediaRecorder from '@webcore/core/mediarecorder/mediarecorder';
import type { MediaRecorderResponse } from '@webcore/core/mediarecorder/mediarecorder.types';

// Content data
import {
  ContentData,
  TURTLE_MESH_SCALE_BY_ID,
  TURTLE_MODEL_SCALE_BY_ID,
} from './data/data';

// Default content ID (first item) - App.tsx will override via 'set-model-id' event if needed
// Models are lazy loaded - no default content at startup

// Preview manager (generic, from boilerplate-core)
import { 
  PreviewManager,
  type FullscreenEvent,
} from '@core/controllers/PreviewViewer';
import { IS_EYEJACK_WEBXR_POLYFILL } from '@webcore/core/utils/utils';

const TURTLE_ALBEDO_SCALE_BY_ID: Partial<Record<string, number>> = {
  hawksbill: 1.5,
};

export default class MainApp extends AppWebXRSimple {
  private wsBridge?: WSBridge | null = null;

  private appState: AppState = AppState.LOADING;
  private xrFloorEstimate?: XRModuleFloorEstimate;
  private previewSuspended = false;
  
  // Content
  private fadeController?: SceneFadeController;
  private readonly turtleFadeInDurationMs = 180;
  private readonly turtleFadeOutDurationMs = 120;
  private turtleInsideLastState = false;
  private turtleCurrentAlpha = 1;
  private turtleAlphaTarget = 1;
  private lastTurtleFadeUpdateMs = 0;
  private readonly turtleMaterialBaseAlpha = new Map<number, number>();
  private xrEnvironmentTexture: CubeTexture | null = null;
  private xrEnvironmentPath?: string;
  
  // Core managers
  private anchorManager?: AnchorManager;
  private placementController?: PlacementController;
  private instanceManager?: ContentInstanceManager;
  private playbackController?: ContentPlaybackController;
  private previewManager?: PreviewManager;
  
  // WebXR media recorder
  private webMediaRecorder?: MediaRecorder;
  
  // Video texture for GLB mesh planes (uses dedicated video element, NOT ejw-video)
  private glbVideoTexture?: VideoTexture;
  private glbVideoElement?: HTMLVideoElement;

  /**
   * XR safety guard: preview skyboxes should never leak into the AR camera view.
   */
  private suppressPreviewEnvironmentInXR(): void {
    if (!this.scene) return;
    for (const mesh of this.scene.meshes) {
      if (mesh.name?.startsWith('skybox_') && mesh.isEnabled()) {
        mesh.setEnabled(false);
      }
    }
  }

  private isEnvironmentTexturePath(path?: string): boolean {
    const normalizedPath = path?.split('?')[0] ?? '';
    return normalizedPath.toLowerCase().endsWith('.env');
  }

  private getCurrentContentEnvironmentPath(): string | undefined {
    const currentContentId = this.instanceManager?.currentInstance?.contentItem?.id;
    if (!currentContentId) return undefined;
    const content = ContentData.find((item) => item.id === currentContentId);
    // Prefer iblEnvironment for XR lighting if available
    return content?.iblEnvironment ?? content?.environment;
  }

  private ensureXREnvironmentTexture(): void {
    if (!this.scene || !this.xrSession?.isXRRunning) return;

    const environmentPath = this.getCurrentContentEnvironmentPath();
    if (!environmentPath || !this.isEnvironmentTexturePath(environmentPath)) {
      this.scene.environmentTexture = null;
      return;
    }

    if (!this.xrEnvironmentTexture || this.xrEnvironmentPath !== environmentPath) {
      this.xrEnvironmentTexture?.dispose();
      this.xrEnvironmentTexture = CubeTexture.CreateFromPrefilteredData(environmentPath, this.scene);
      this.xrEnvironmentTexture.name = 'xr_environment';
      // Blur the IBL reflections by offsetting to higher mip levels
      this.xrEnvironmentTexture.lodGenerationOffset = 3;
      this.xrEnvironmentPath = environmentPath;
    }

    this.scene.environmentTexture = this.xrEnvironmentTexture;
  }

  constructor() {
    const renderCanvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
    super(renderCanvas);
    
    this.placementController = new PlacementController({
      minDistance: 1,  
      maxDistance: 8,
      gestures: { disableScale: true, disableRotation: true },
    });
    
    // Listen for placement confirmation from UI (AROverlay)
    appEvents.on('content-positioned', () => {
      this.confirmPlacement();
    });

    // WSBridge must be initialized in constructor so window.EJFrameStart/EJFrameEnd
    // are registered before the native polyfill starts its frame loop (when XR begins)
    this.wsBridgeInit();
  }

  protected override initialize(): void {
    super.initialize();
    this.setupEventListeners();

    if (this.engine && this.renderCanvas) {
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      this.engine.setHardwareScalingLevel(1 / dpr);
    }
  }
  
  protected override async initXR(): Promise<void> {
    const { isMobile } = getUserDevice();
    if (!isMobile) {
      return;
    }
    // Skip XR init when WebXR isn't available (e.g. Safari). App Clip flow handles AR on iOS.
    if (typeof navigator !== 'undefined' && !navigator.xr) {
      return;
    }
    return super.initXR();
  }

  /**
   * Initialize WSBridge for EyeJack WebXR polyfill camera access.
   * Must run in constructor so window.EJFrameStart/EJFrameEnd are set before
   * the native app starts its frame loop when user enters XR.
   */
  private wsBridgeInit() {
    if (!IS_EYEJACK_WEBXR_POLYFILL) {
      return;
    }
    if (this.wsBridge) {
      return;
    }

    const options: WSBridgeOptions = {
      frameStartCallback: this.wsBridgeFrameStart.bind(this),
      frameEndCallback: this.wsBridgeFrameEnd.bind(this),
    };
    this.wsBridge = new WSBridge(options);
    this.wsBridge.cameraDataUpdatePolicy = WSBridgeDataUpdatePolicy.Always;
    this.wsBridge.cameraImageType = WSBridgeCameraImageType.JPEG;
    this.wsBridge.cameraImageScale = 0.25;
    this.wsBridge.cameraImageQuality = 0.75;
    this.wsBridge.init();
    console.log('wsBridge initialized');
  }

  private wsBridgeFrameStart() {
    // Optional: add stats or per-frame logic here (demo uses for Stats.js)
  }

  private wsBridgeFrameEnd() {
    // Optional: add stats or per-frame logic here (demo uses for Stats.js)
  }

  private setupEventListeners(): void {
    // Note: 'xr-stop' is handled by base class which calls stopXR()
    
    (emitter as any).on('reposition', () => this.resetPlacement());

    // Listen for content-loaded to show model and configure gestures if we're in XR
    // This handles the case where XR started before the model finished loading (iOS App Clip)
    (emitter as any).on('content-loaded', (contentId: string) => {
      const currentInstance = this.instanceManager?.currentInstance;
      // Note: contentId is the instance ID (e.g., "leatherback_1770080860031")
      // Match against currentInstance.id, not contentItem.id
      if (this.appState === AppState.XR_PLACING && 
          currentInstance?.isLoaded && 
          currentInstance.id === contentId) {
        this.ensureXREnvironmentTexture();
        
        // Parent model to ground anchor if not already
        const groundAnchor = this.anchorManager?.getGroundAnchor();
        if (groundAnchor && currentInstance.rootNode.parent !== groundAnchor) {
          currentInstance.rootNode.parent = groundAnchor;
          currentInstance.rootNode.position.set(0, 0, 0);
        }
        
        // Make model visible (faded at 0.5 alpha via fadeController)
        currentInstance.rootNode.setEnabled(true);
        for (const asset of currentInstance.assets) {
          if (asset.type === 'model') {
            const modelAsset = asset as IAssetModel;
            if (modelAsset.meshRoot) {
              modelAsset.meshRoot.setEnabled(true);
            }
            if (modelAsset.meshes) {
              for (const mesh of modelAsset.meshes) {
                mesh.setEnabled(true);
                mesh.isVisible = true;
              }
            }
          }
        }
        
        // Configure gestures from content arGestureOptions
        this.placementController?.configureFromContent(currentInstance);

        const isWaitingForVideoFrame = this.setupVideoTextureForCurrentContent();
        if (!isWaitingForVideoFrame) {
          this.emitContentVisualReady(currentInstance.contentItem?.id);
        }
      }
    });

    // Listen for model selection from UI (by ID)
    (emitter as any).on('set-model-id', async (modelId: string) => {
      const instance = this.instanceManager?.getInstance(modelId);
      if (instance?.isLoaded) {
        // Model already in scene from preview - just set as active
        this.instanceManager?.setActiveInstance(modelId);
        // Emit content-loaded so UI knows model is ready
        emitter.emit('content-loaded', modelId);
      } else {
        // Model not loaded yet - load it and wait for it to be set as current
        await this.instanceManager?.switchContent(modelId);
        // Note: content-loaded will be emitted by ContentInstanceManager after loading completes
      }
    });
  }
  
  /**
   * Setup event listeners for canvas mount/unmount from usePreview hook
   */
  private setupPreviewEventListeners(): void {
    // Canvas mounted - look up config from ContentData and register with PreviewManager
    (emitter as any).on('canvas-mount', (event: { canvas: HTMLCanvasElement; contentId: string }) => {
      if (!this.previewManager) return;
      if (this.previewSuspended) return;
      const { isMobile } = getUserDevice();
      
      // Look up content config from ContentData
      const content = ContentData.find(c => c.id === event.contentId);
      if (!content) {
        console.warn(`Content not found for ID: ${event.contentId}`);
        return;
      }

      // Build config from ContentData
      this.previewManager.add(event.canvas, {
        contentId: event.contentId,
        environmentPath: content.environment,
        iblEnvironmentPath: content.iblEnvironment,
        rotationY: (content.viewerRotationY ?? 0),
        viewerOffset: content.viewerOffset,
        excludeMeshes: content.excludeMeshes,
        includeMeshes: content.includeMeshes ?? undefined,
        rotation: 'horizontal',
        zoom: false,
        allowPageScroll: isMobile,
      });
    });
    
    // Canvas unmounted - remove from PreviewManager
    (emitter as any).on('canvas-unmount', (event: { canvas: HTMLCanvasElement }) => {
      if (!this.previewManager) return;
      this.previewManager.remove(event.canvas);
    });
  }
  
  // ============================================================================
  // Content Instance Manager Setup
  // ============================================================================
  
  private initContentManager(): void {
    if (!this.scene || !this.engine) {
      console.error("Scene or Engine not available");
      return;
    }

    this.instanceManager = new ContentInstanceManager(
      ContentData,
      this.scene,
      this.loadContents.bind(this),
      this.unloadContents.bind(this),
      (idOrPath: string) => this.assets.find(a => a.id === idOrPath || a.path === idOrPath)
    );
    
    // Create PreviewManager instance
    this.previewManager = new PreviewManager(
      this.engine,
      this.scene,
      this.instanceManager,
      {
        lazyLoad: true,
        fullscreen: true,
        rootMargin: '100px',
        unloadOnDetach: false,
        onViewerEvent: (canvas, event) => {
          (emitter as any).emit('preview-event', {
            canvas,
            type: event.type,
            error: event.type === 'error' ? event.error : undefined,
          });
        },
        onFullscreenChange: (event: FullscreenEvent) => {
          (emitter as any).emit('preview-fullscreen', event);
        },
      }
    );
    
    // Listen for preview attach/detach events from useSharedViewer hook
    this.setupPreviewEventListeners();
    
    // Setup render loop with multi-view support
    // The callback determines when to render main canvas (only when in AR, not preview mode)
    this.engine.stopRenderLoop();
    const renderCallback = this.previewManager.createRenderCallback(
      () => {
        this.render();
        this.scene!.render();
      },
      () => this.appState !== AppState.READY // Render main when in AR (not in READY/preview state)
    );
    this.engine.runRenderLoop(renderCallback);
    
    this.playbackController = new ContentPlaybackController({
      onLoopComplete: () => this.instanceManager?.restartCurrent()
    });
    
    // Initialize without default content - models are lazy loaded via preview or AR launch
    this.instanceManager.initialize(this.playbackController, {
      autoPlay: false,
    });
  }
  
  protected override loadAssetComplete(asset: IAssetBase): void {
    this.instanceManager?.onAssetLoadComplete(asset);
    
    if (asset.type === 'model') {
      const modelAsset = asset as IAssetModel;
      const meshes = modelAsset.meshes!;
      const root = modelAsset.meshRoot;

      // Apply turtle-specific scaling based on content id resolved from model path.
      const normalizePath = (path: string): string => path.split('?')[0];
      const content = ContentData.find((item) =>
        item.content?.some(
          (contentItem) => {
            const contentPath = typeof contentItem.path === 'string' ? contentItem.path : '';
            return contentItem.type === 'model' &&
              normalizePath(contentPath) === normalizePath(asset.path);
          }
        )
      );
      const contentId = content?.id;
      const includePatterns = content?.includeMeshes?.map((name) => name.toLowerCase()) ?? [];
      const albedoScale = contentId ? TURTLE_ALBEDO_SCALE_BY_ID[contentId] : undefined;

      if (contentId) {
        const wholeModelScale = TURTLE_MODEL_SCALE_BY_ID[contentId];
        if (wholeModelScale !== undefined) {
          // Scale the model wrapper when available so the full model scales uniformly.
          const scaleTarget = modelAsset.meshRoot?.parent ?? modelAsset.meshRoot;
          if (scaleTarget && 'scaling' in scaleTarget) {
            scaleTarget.scaling.set(wholeModelScale, wholeModelScale, wholeModelScale);
          }
          console.log('[ScaleApplied]', {
            turtleId: contentId,
            mode: 'whole-model',
            scale: wholeModelScale,
            includeMeshes: content?.includeMeshes ?? [],
          });
        } else {
          const meshScale = TURTLE_MESH_SCALE_BY_ID[contentId];
          if (meshScale !== undefined && includePatterns.length > 0 && modelAsset.meshes) {
            const scaledMeshNames: string[] = [];
            for (const mesh of modelAsset.meshes) {
              console.log('[MeshName]', mesh.name);
              const meshName = mesh.name.toLowerCase();
              if (includePatterns.some((pattern) => meshName.includes(pattern))) {
                mesh.scaling.set(meshScale, meshScale, meshScale);
                scaledMeshNames.push(mesh.name);
              }
            }
            console.log('[ScaleApplied]', {
              turtleId: contentId,
              mode: 'mesh-only',
              scale: meshScale,
              includeMeshes: content?.includeMeshes ?? [],
              scaledMeshes: scaledMeshNames,
            });
          } else {
            console.log('[ScaleApplied]', {
              turtleId: contentId,
              mode: 'none',
              reason: 'No matching scale entry or includeMeshes not set',
              includeMeshes: content?.includeMeshes ?? [],
            });
          }
        }
      }

      const panelsParent = root?.getChildren(n => n.name.includes("Panels"), false)[0];
      
      for (const mesh of meshes) {
        const meshName = mesh.name.toLowerCase();
        const isIncludedTurtleMesh =
          includePatterns.length > 0 &&
          includePatterns.some((pattern) => meshName.includes(pattern));
        if (isIncludedTurtleMesh) {
          this.applyAlbedoScale(mesh.material, albedoScale);
        }

        const isButchullaPanelMesh = contentId === 'butchulla' && mesh.name !== 'VideoPlane';
        const isNamedPanelMesh =
          !!panelsParent && mesh.isDescendantOf(panelsParent) ||
          mesh.name.includes("Panels") ||
          mesh.name.includes("Panel");

        if ((isButchullaPanelMesh || isNamedPanelMesh) && mesh.material instanceof PBRMaterial) {
          mesh.material.unlit = true;
        }
        mesh.renderingGroupId = mesh.name.includes("Background") ? 0 : 1;
      }
      
      // Apply video texture to VideoPlane mesh if content has videoTexturePath
      // Only set up video during XR session to avoid spurious plays during preview
      const videoPath = (content as any)?.videoTexturePath;
      if (videoPath && contentId) {
        const videoPlane = meshes.find(m => m.name === 'VideoPlane');
        if (videoPlane && this.xrSession?.isXRRunning) {
          this.setupVideoPlaneTexture(videoPlane, videoPath, contentId);
        }
      }
    }
  }
  
  private emitContentVisualReady(contentId?: string): void {
    if (!contentId) return;
    (emitter as any).emit('content-visual-ready', contentId);
  }

  private waitForGlbVideoFrame(video: HTMLVideoElement, contentId: string): void {
    const notifyReady = () => {
      if (this.instanceManager?.currentInstance?.contentItem?.id !== contentId) {
        return;
      }
      this.emitContentVisualReady(contentId);
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      notifyReady();
      return;
    }

    const cleanup = () => {
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('error', handleError);
    };

    const handleReady = () => {
      cleanup();
      notifyReady();
    };

    const handleError = () => {
      cleanup();
      console.warn(`[VideoPlane] Failed to warm video texture for ${contentId}`);
      notifyReady();
    };

    video.addEventListener('loadeddata', handleReady, { once: true });
    video.addEventListener('canplay', handleReady, { once: true });
    video.addEventListener('error', handleError, { once: true });
  }

  private setupVideoTextureForCurrentContent(): boolean {
    const currentInstance = this.instanceManager?.currentInstance;
    if (!currentInstance?.isLoaded) return false;
    
    const contentId = currentInstance.contentItem?.id;
    const content = ContentData.find((item) => item.id === contentId);
    const videoPath = (content as any)?.videoTexturePath;
    
    if (!videoPath || !contentId) return false;
    
    // Find VideoPlane mesh in current instance's model assets
    for (const asset of currentInstance.assets) {
      if (asset.type === 'model') {
        const modelAsset = asset as IAssetModel;
        const videoPlane = modelAsset.meshes?.find(m => m.name === 'VideoPlane');
        if (videoPlane) {
          console.log(`[VideoPlane] Setting up video for ${contentId} on XR entry`);
          this.setupVideoPlaneTexture(videoPlane, videoPath, contentId);
          return true;
        }
      }
    }
    
    console.log(`[VideoPlane] No VideoPlane mesh found for ${contentId}`);
    return false;
  }
  
  private setupVideoPlaneTexture(mesh: AbstractMesh, videoPath: string, contentId: string): void {
    if (!this.scene) return;
    
    // Dispose previous video texture if any
    if (this.glbVideoTexture) {
      this.glbVideoTexture.dispose();
      this.glbVideoTexture = undefined;
    }

    // Create or reuse dedicated video element for GLB planes (separate from ejw-video)
    const video = this.getOrCreateGlbVideoElement();
    video.pause();
    video.src = videoPath;
    this.waitForGlbVideoFrame(video, contentId);
    video.load();
    this.stopGlbVideo();
    
    const videoTexture = new VideoTexture(
      `video_${contentId}`,
      video,
      this.scene,
      false,  // generateMipMaps
      true,   // invertY
      undefined, // samplingMode (default)
      { autoPlay: false, autoUpdateTexture: true }
    );
    
    // Ensure video stays paused after VideoTexture creation
    video.pause();
    
    // Store reference for playback control
    this.glbVideoTexture = videoTexture;
    
    const material = new StandardMaterial(`videoMat_${contentId}`, this.scene);
    material.diffuseTexture = videoTexture;
    material.emissiveTexture = videoTexture;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    
    mesh.material = material;
    console.log(`[VideoPlane] Applied video texture to "${mesh.name}"`);
  }
  
  private getOrCreateGlbVideoElement(): HTMLVideoElement {
    if (this.glbVideoElement) {
      return this.glbVideoElement;
    }
    
    // Create a dedicated video element for GLB plane textures
    const video = document.createElement('video');
    video.id = 'glb-video-plane';
    video.style.display = 'none';
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    document.body.appendChild(video);
    
    this.glbVideoElement = video;
    return video;
  }

  private seekGlbVideoToStart(video: HTMLVideoElement): void {
    const seekToStart = () => {
      try {
        video.currentTime = 0;
      } catch (error) {
        console.warn('Failed to seek GLB video to start:', error);
      }
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekToStart();
      return;
    }

    const handleLoadedMetadata = () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      seekToStart();
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
  }
  
  private playGlbVideo(): void {
    const video = this.glbVideoElement;
    if (!video) return;
    video.muted = false;
    video.volume = 1;
    video.play().catch(e => {
      console.warn('Video play with audio failed, trying muted:', e);
      video.muted = true;
      video.play().catch(e2 => console.warn('Video play failed:', e2));
    });
  }
  
  private pauseGlbVideo(): void {
    const video = this.glbVideoElement;
    if (!video) return;
    video.pause();
  }
  
  private stopGlbVideo(): void {
    const video = this.glbVideoElement;
    if (!video) return;
    video.pause();
    this.seekGlbVideoToStart(video);
  }
  
  private cleanupGlbVideo(): void {
    // Dispose video texture
    if (this.glbVideoTexture) {
      this.glbVideoTexture.dispose();
      this.glbVideoTexture = undefined;
    }
    
    // Reset video element but keep it around for reuse
    const video = this.glbVideoElement;
    if (video) {
      video.pause();
      video.src = '';
      video.load();
    }
  }

  private applyAlbedoScale(
    material?: Material | null,
    albedoScale?: number,
    processed = new Set<number>()
  ): void {
    if (!material || albedoScale === undefined || processed.has(material.uniqueId)) return;
    processed.add(material.uniqueId);

    if (material instanceof MultiMaterial) {
      for (const subMaterial of material.subMaterials) {
        if (subMaterial) {
          this.applyAlbedoScale(subMaterial, albedoScale, processed);
        }
      }
      return;
    }

    if (!(material instanceof PBRMaterial)) return;

    material.albedoColor = material.albedoColor.scale(albedoScale);
  }

  private getCurrentModelMeshes(): AbstractMesh[] {
    const currentInstance = this.instanceManager?.currentInstance;
    if (!currentInstance?.isLoaded) return [];

    const meshes: AbstractMesh[] = [];
    for (const asset of currentInstance.assets) {
      if (asset.type !== 'model') continue;
      const modelAsset = asset as IAssetModel;
      if (modelAsset.meshes) {
        meshes.push(...modelAsset.meshes);
      }
    }
    return meshes;
  }

  private getCurrentIncludeMeshPatterns(): string[] {
    const currentInstance = this.instanceManager?.currentInstance;
    const includeMeshes = (currentInstance?.contentItem as { includeMeshes?: string[] } | undefined)?.includeMeshes;
    return includeMeshes?.map((name) => name.toLowerCase()) ?? [];
  }

  private getCurrentIncludedTurtleMeshes(): AbstractMesh[] {
    const includePatterns = this.getCurrentIncludeMeshPatterns();
    if (includePatterns.length === 0) return [];
    return this.getCurrentModelMeshes().filter((mesh) => {
      if (!mesh.isEnabled() || !mesh.isVisible) return false;
      const meshName = mesh.name.toLowerCase();
      return includePatterns.some((pattern) => meshName.includes(pattern));
    });
  }

  private applyProximityFade(): void {
    if (!this.scene || !this.camera) return;

    const meshes = this.getCurrentIncludedTurtleMeshes();
    if (!meshes.length) {
      this.turtleInsideLastState = false;
      this.turtleAlphaTarget = 1;
      this.updateTurtleMeshAlpha([], this.turtleAlphaTarget);
      return;
    }

    let nearestSignedDistance = Number.POSITIVE_INFINITY;
    const activeCamera = this.scene.activeCamera ?? this.camera;
    const cameraPosition = activeCamera.globalPosition;
    for (const mesh of meshes) {
      const box = mesh.getBoundingInfo().boundingBox;
      const center = box.centerWorld;
      const extents = box.extendSizeWorld;
      const signedDistance = this.signedDistanceToBoundingBox(cameraPosition, center, extents);
      if (signedDistance < nearestSignedDistance) {
        nearestSignedDistance = signedDistance;
      }
    }

    const isInsideTurtle = nearestSignedDistance <= 0;
    if (isInsideTurtle !== this.turtleInsideLastState) {
      this.turtleInsideLastState = isInsideTurtle;
    }

    this.turtleAlphaTarget = isInsideTurtle ? 0 : 1;
    this.updateTurtleMeshAlpha(meshes, this.turtleAlphaTarget);
  }

  private signedDistanceToBoundingBox(point: Vector3, center: Vector3, extents: Vector3): number {
    // Signed distance to AABB:
    // > 0 = outside, 0 = touching surface, < 0 = inside.
    const qx = Math.abs(point.x - center.x) - extents.x;
    const qy = Math.abs(point.y - center.y) - extents.y;
    const qz = Math.abs(point.z - center.z) - extents.z;

    const ox = Math.max(qx, 0);
    const oy = Math.max(qy, 0);
    const oz = Math.max(qz, 0);
    const outsideDistance = Math.sqrt((ox * ox) + (oy * oy) + (oz * oz));
    const insideDistance = Math.min(Math.max(qx, Math.max(qy, qz)), 0);

    return outsideDistance + insideDistance;
  }

  private updateTurtleMeshAlpha(meshes: AbstractMesh[], targetAlpha: number): void {
    const now = performance.now();
    if (this.lastTurtleFadeUpdateMs <= 0) {
      this.lastTurtleFadeUpdateMs = now;
    }
    const dt = Math.max(0, now - this.lastTurtleFadeUpdateMs);
    this.lastTurtleFadeUpdateMs = now;

    const duration = targetAlpha < this.turtleCurrentAlpha
      ? this.turtleFadeOutDurationMs
      : this.turtleFadeInDurationMs;
    const step = duration > 0 ? Math.min(1, dt / duration) : 1;
    this.turtleCurrentAlpha = this.turtleCurrentAlpha + ((targetAlpha - this.turtleCurrentAlpha) * step);
    if (Math.abs(this.turtleCurrentAlpha - targetAlpha) < 0.01) {
      this.turtleCurrentAlpha = targetAlpha;
    }

    const processed = new Set<number>();
    for (const mesh of meshes) {
      const material = mesh.material;
      if (!material) continue;
      if (material instanceof MultiMaterial) {
        for (const subMaterial of material.subMaterials) {
          if (!subMaterial || processed.has(subMaterial.uniqueId)) continue;
          processed.add(subMaterial.uniqueId);
          this.applyMaterialAlpha(subMaterial, this.turtleCurrentAlpha);
        }
      } else {
        if (processed.has(material.uniqueId)) continue;
        processed.add(material.uniqueId);
        this.applyMaterialAlpha(material, this.turtleCurrentAlpha);
      }
    }
  }

  private applyMaterialAlpha(material: Material, alphaMultiplier: number): void {
    if (!this.turtleMaterialBaseAlpha.has(material.uniqueId)) {
      this.turtleMaterialBaseAlpha.set(material.uniqueId, material.alpha);
    }
    const baseAlpha = this.turtleMaterialBaseAlpha.get(material.uniqueId) ?? 1;
    material.alpha = baseAlpha * alphaMultiplier;
    if (material instanceof PBRMaterial) {
      material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
    }
  }
  
  /**
   * Initialize fade controller for XR mode
   * Called when entering XR to apply fade effect (not during preview)
   */
  private initFadeControllerForXR(): void {
    if (this.fadeController || !this.scene || !this.camera) return;
    
    this.fadeController = new SceneFadeController(
      this.scene,
      this.scene.getEngine() as Engine,
      this.camera
    );
    
    // Start at 0.5 for placement mode
    this.fadeController.initialize(0.5);
  }
  
  // ============================================================================
  // Placement Methods 
  // ============================================================================
  
  public confirmPlacement(): void {
    // Only confirm if we're actually placing
    if (!this.placementController?.isPlacing()) return;
    
    // Confirm placement on the controller
    this.placementController.confirmPlacement();
    
    this.fadeController?.fade(1, 400);
    this.appState = AppState.EXPERIENCE;
    emitter.emit('content-ready', 'model');
    appEvents.emit('app-state-changed', AppState.EXPERIENCE);
    
    // Start video playback for GLB video planes
    this.playGlbVideo();
  }
  
  public resetPlacement(): void {
    if (!this.xrSession?.isXRRunning) return;
    
    // Pause video during repositioning
    this.pauseGlbVideo();
    
    this.placementController?.reposition();
    this.fadeController?.fade(0.5, 0);
    this.appState = AppState.XR_PLACING;
    appEvents.emit('app-state-changed', AppState.XR_PLACING);
    
    // Notify UI to show placement overlay again
    (emitter as any).emit('content-repositioning');
  }
  
  private setupInteraction(): void {
    // Use DOM overlay for WebXR - this is where touch events go
    // Disable tap-to-place since AROverlay handles it
    const domOverlay = document.getElementById('domOverlay');
    this.placementController?.setInteractionTarget(domOverlay ?? undefined, { tapToPlace: false });
  }
  
  private cleanupInteraction(): void {
    this.placementController?.clearInteractionTarget();
  }

  // ============================================================================
  // XR Setup
  // ============================================================================

  protected override initXROptions(): XRModuleSessionOptions {
    const options = new XRModuleSessionOptions();
    options.disableDefaultUI = true;
    options.domOverlayElementName = '#domOverlay';
    options.domOverlayElement = document.getElementById('domOverlay') as Element;
    
    options.onInXR = () => {
      if (!this.scene) {
        console.error('MainApp.onInXR: scene is undefined');
        return;
      }
      
      // Reset video to start when entering XR
      this.stopGlbVideo();
      
      this.previewSuspended = true;
      this.suppressPreviewEnvironmentInXR();
      
      this.appState = AppState.XR_PLACING;
      
      // Initialize fade controller for XR (first time only)
      // This sets up the fade effect and changes mesh layerMasks
      this.initFadeControllerForXR();
      
      // Initialize placement controller with scene and ground anchor
      const groundAnchor = this.anchorManager?.createGroundAnchor();
      this.placementController?.initialize(this.scene, groundAnchor);
      
      // Start placement mode
      this.placementController?.startPlacement();
      
      // Set up tap to place
      this.setupInteraction();
      
      // Get the current instance before disposing others
      const currentInstance = this.instanceManager?.currentInstance;
      const currentContentId = currentInstance?.contentItem?.id;
      
      // Dispose ALL other models to free memory - only keep the active one
      const allInstances = this.instanceManager?.getAllInstances() || [];
      for (const inst of allInstances) {
        if (inst.contentItem?.id !== currentContentId) {
          this.instanceManager?.disposeInstanceById(inst.contentItem?.id || '');
        }
      }
      
      // Show the current model (visible but not playing)
      this.instanceManager?.showCurrent();
      this.ensureXREnvironmentTexture();
      
      // Stop any animations that might be playing from preview
      // (animations should only play after placement is confirmed)
      this.instanceManager?.stopCurrent();
      
      // Ensure model is visible and parented to ground anchor
      if (currentInstance?.isLoaded) {
        // Re-parent the model to the ground anchor if it was loaded for preview
        if (groundAnchor && currentInstance.rootNode.parent !== groundAnchor) {
          currentInstance.rootNode.parent = groundAnchor;
          currentInstance.rootNode.position.set(0, 0, 0);
        }
        
        // Make meshes visible
        currentInstance.rootNode.setEnabled(true);
        for (const asset of currentInstance.assets) {
          if (asset.type === 'model') {
            const modelAsset = asset as any;
            if (modelAsset.meshRoot) {
              modelAsset.meshRoot.setEnabled(true);
            }
            if (modelAsset.meshes) {
              for (const mesh of modelAsset.meshes) {
                mesh.setEnabled(true);
                mesh.isVisible = true;
              }
            }
          }
        }
        
        // Configure gestures from content arGestureOptions (handled by boilerplate)
        this.placementController?.configureFromContent(currentInstance);
      }
      
      // Initialize media recorder for WebXR
      if (this.xr && this.scene) {
        this.webMediaRecorder = new MediaRecorder();
        this.webMediaRecorder.initWeb(this.scene, this.xr);
      }
      
      appEvents.emit('app-state-changed', AppState.XR_PLACING);
      
      // Signal content is loaded if model is already ready
      if (this.instanceManager?.currentInstance?.isLoaded) {
        emitter.emit('content-loaded', this.instanceManager.currentInstance.id);
      } 
    };
    
    options.onExitingXR = () => {
      this.appState = AppState.READY;
      
      // Stop and clean up GLB video
      this.stopGlbVideo();
      this.cleanupGlbVideo();
      
      // Dispose fade controller to restore mesh layerMasks for preview
      if (this.fadeController) {
        this.fadeController.dispose();
        this.fadeController = undefined;
      }
      this.turtleCurrentAlpha = 1;
      this.turtleAlphaTarget = 1;
      this.turtleInsideLastState = false;
      this.lastTurtleFadeUpdateMs = 0;
      
      this.cleanupInteraction();
      this.placementController?.reset();
      this.xrFloorEstimate = undefined;
      this.previewSuspended = false;
      if (this.scene) {
        this.scene.environmentTexture = null;
      }
      
      // Restart preview canvas observation after XR exit
      this.previewManager?.restart();
      
      emitter.emit('xr-exited');
      appEvents.emit('app-state-changed', AppState.READY);
    };
    
    return options;
  }

  protected override initCustom(): void {
    // Lighting
    const light = new HemisphericLight('ambientLight', new Vector3(0, 1, 0), this.scene);
    light.intensity = 1.2;
    light.groundColor = new Color3(0.4, 0.4, 0.5);

    // Initialize anchor manager
    this.anchorManager = new AnchorManager(this.scene!);
    
    // Initialize content manager
    this.initContentManager();
  }

  public override async startXR(): Promise<void> {
    this.previewSuspended = true;

    // Dispose preview viewers - cleans up cameras, skyboxes, non-active instances
    // The current instance is protected in detach() and won't be disposed
    // restart() will re-observe canvases from registeredConfigs after XR exit
    this.previewManager?.dispose();
    this.suppressPreviewEnvironmentInXR();
    
    try {
      if (!this.xrSession) {
        await this.initXR();
      }
      await this.xrSession?.startXR();
    } catch (error) {
      this.previewSuspended = false;
      console.error('Failed to start XR session:', error);
      emitter.emit('xr-error-not-supported');
    }
  }

  public override async stopXR(): Promise<void> {
    try {
      await this.xrSession?.stopXR();
    } catch (error) {
      console.error('Failed to stop XR session:', error);
    }
  }

  // ============================================================================
  // Recording - Override base class methods to use WebXR media recorder
  // app.base.ts handles record-start/record-stop events and calls these methods
  // ============================================================================

  public override async recordStartAsync(): Promise<MediaRecorderResponse> {
    if (!this.webMediaRecorder) {
      return { success: false, error: 'Media recorder not initialized', url: undefined };
    }
    // Delay to allow UI to fade out before recording starts
    await new Promise(resolve => setTimeout(resolve, 400));
    return this.webMediaRecorder.start();
  }

  public override async recordStopAsync(): Promise<MediaRecorderResponse> {
    if (!this.webMediaRecorder) {
      return { success: false, error: 'Media recorder not initialized', url: undefined };
    }
    return this.webMediaRecorder.stop();
  }

  public override async loadAsync(): Promise<void> {
    // Content is loaded via ContentInstanceManager
    this.appState = AppState.READY;
    emitter.emit('loaded');
  }

  // ============================================================================
  // Render Loop
  // ============================================================================

  public override render(): void {
    if (!this.xrSession?.isXRRunning || !this.scene) return;

    // Keep XR immune to any late preview env/skybox writes.
    this.suppressPreviewEnvironmentInXR();
    this.ensureXREnvironmentTexture();

    // Initialize floor estimate
    if (!this.xrFloorEstimate && this.xr) {
      const options = new XRModuleFloorEstimateOptions();
      options.initialFloorEstimate = -1.0;
      this.xrFloorEstimate = new XRModuleFloorEstimate(this.xr, this.scene);
      this.xrFloorEstimate.initFloorEstimate(options);
    }

    // Update floor estimate
    this.xrFloorEstimate?.updateFloorEstimate();
    const floorY = this.xrFloorEstimate?.getFloorEstimateSmoothed() ?? 0;

    // Update placement controller
    if (this.placementController?.isPlacing()) {
      this.placementController.setGroundY(floorY);
      this.placementController.update();
    }

    // Fade model as the camera gets too close/inside to avoid interior artefacts.
    this.applyProximityFade();
  }

  public getAppState(): AppState {
    return this.appState;
  }

  public isReady(): boolean {
    return this.appState === AppState.READY || this.appState === AppState.EXPERIENCE;
  }
}

// Initialize app
const app = new MainApp();
app.init();
app.loadAsync();

export { app };
